








import './ChromaKey.scss'
import { defineComponent } from '@vue/composition-api'
import helpers from '../../helpers'
import { ChromaKeyPreviewQualityType } from '../../types/users'
import { ChromaKey } from '@/modules/user/classes'

export default defineComponent({
  name: 'ChromaKey',
  props: {
    videoId: {
      type: String,
      required: true
    },
    chromakey: {
      type: Object as () => ChromaKey,
      default: null
    },
    pickingMode: {
      type: Boolean,
      default: false
    },
    fullres: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      chromaKeyPreviewQualityValue: ChromaKeyPreviewQualityType.Low as ChromaKeyPreviewQualityType,
      stopRendering: false,
      gl: null as any,
      chromacanvas: null as any,
      video: null as any,
      deleteme: null as any,
      shaderProgram: null as any,

      source: null as any,
      vertexBuffer: null as any,
      indexBuffer: null as any,
      textureCoordBuffer: null as any,

      texture: null as any,
      indices: [] as Array<number>,

      enabled: null as any,
      screen: null as any,
      saturationThreshold: null as any,
      fmaxThreshold: null as any,
      clipBlack: null as any,
      clipWhite: null as any,

      initialized: false as any,
      initialized2: 0 as any,
      contextLost: false as any,

      lastrender: 0 as number,
      lasttext: 0 as number
    }
  },
  watch: {
    dbChromaKeyPreviewQualityValue (newVal) {
      if (newVal && newVal != this.chromaKeyPreviewQualityValue) {
        this.chromaKeyPreviewQualityValue = newVal
        if (this.initialized) {
          this.updateCanvasSize()
        }
      }
    }
  },
  computed: {
    isPickingMode (): boolean {
      return this.pickingMode
    },
    dbChromaKeyPreviewQualityValue (): ChromaKeyPreviewQualityType | null | undefined {
      return this.$store.direct.state.user.profile?.chromaKeyPreviewQuality
    },
    vertexShaderSrc (): String {
      const shader = [
        'precision mediump float;',

        'attribute vec3 coordinates;',
        'varying vec2 vTexCoord;',
        'attribute vec2 texCoord;',

        'uniform vec4 screen;',
        'varying float screenSat;',
        'varying float screenHue;',

        'void main(void) {',
        'float fmin, fmax;',
        'if (screen.r < screen.g ) {',
        '  fmin = min(screen.r, screen.b);',
        '  if (screen.g < screen.b ) {',
        '    fmax = screen.b;',
        '     screenHue = 60.0 * ((screen.r - screen.g) / (fmax - fmin)) + 240.0;',
        '  } else {',
        '    fmax = screen.g;',
        '    screenHue = -60.0 * ((screen.r - screen.b) / (fmax - fmin)) + 120.0;',
        '  }',
        '} else {',
        '  fmin = min(screen.g, screen.b);',
        '  if (screen.r < screen.b ) {',
        '    fmax = screen.b;',
        '     screenHue = 60.0 * ((screen.r - screen.g) / (fmax - fmin)) + 240.0;',
        '  } else {',
        '    fmax = screen.r;',
        '    screenHue = 60.0 * ((screen.g - screen.b) / (fmax - fmin)) + 360.0;',
        '  }',
        '}',
        'if (screenHue >= 360.0) screenHue = screenHue - 360.0;',
        'screenSat = ((fmax - fmin) / fmax) / 2.0;',

        // convert back to OpenGL coords
        'gl_Position = vec4(coordinates, 1.0);',
        'vTexCoord = texCoord;',
        '}'
      ].join('\n')
      return shader
    },
    fragmentShaderSrc (): String {
      const shader = [
        'precision mediump float;',

        'varying vec2 vTexCoord;',
        'uniform sampler2D source;',

        'uniform vec4 screen;',
        'uniform float saturationThreshold;',
        'uniform float fmaxThreshold;',
        'uniform float clipBlack;',
        'uniform float clipWhite;',
        'uniform int enabled;',

        'varying float screenSat;',
        'varying float screenHue;',

        'void main(void) {',
        'float pixelSat;',
        'vec4 sourcePixel = texture2D(source, vTexCoord);',
        'if (enabled == 0) {',
        '  gl_FragColor = vec4(sourcePixel.rgb, 1.0);',
        '  return;',
        '}',

        'float fmin, fmax, hue;',
        'if (sourcePixel.r < sourcePixel.g ) {',
        '  fmin = min(sourcePixel.r, sourcePixel.b);',
        '  if (sourcePixel.g < sourcePixel.b ) {',
        '    fmax = sourcePixel.b;',
        '     hue = 60.0 * ((sourcePixel.r - sourcePixel.g) / (fmax - fmin)) + 240.0;',
        '  } else {',
        '    fmax = sourcePixel.g;',
        '    hue = -60.0 * ((sourcePixel.r - sourcePixel.b) / (fmax - fmin)) + 120.0;',
        '  }',
        '} else {',
        '  fmin = min(sourcePixel.g, sourcePixel.b);',
        '  if (sourcePixel.r < sourcePixel.b ) {',
        '    fmax = sourcePixel.b;',
        '     hue = 60.0 * ((sourcePixel.r - sourcePixel.g) / (fmax - fmin)) + 240.0;',
        '  } else {',
        '    fmax = sourcePixel.r;',
        '    hue = 60.0 * ((sourcePixel.g - sourcePixel.b) / (fmax - fmin)) + 360.0;',
        '  }',
        '}',
        'if (hue >= 360.0) hue = hue - 360.0;',
        'pixelSat = ((fmax - fmin) / fmax) / 2.0;',

        'float diffPrimary = abs(hue - screenHue);',
        'if (diffPrimary >= 270.0) diffPrimary = abs(360.0 - diffPrimary);',
        'diffPrimary = (diffPrimary > 40.0 ? 1.0 : 0.0);',

        'float solid = step(1.0, step(pixelSat, saturationThreshold) + step(fmax, fmaxThreshold) + diffPrimary);',

        /*
        Semi-transparent pixel if the primary component matches but if saturation is less
        than that of screen color. Otherwise totally transparent
        */
        'float alpha = max(0.0, 1.0 - pixelSat / screenSat);',
        'alpha = smoothstep(clipBlack, clipWhite, alpha);',
        'vec4 semiTransparentPixel = vec4((sourcePixel.rgb - (1.0 - alpha) * screen.rgb) / max(0.00001, alpha), alpha);',
        'gl_FragColor = mix(semiTransparentPixel, sourcePixel, solid);',
        '}'
      ].join('\n')
      return shader
    }
  },
  mounted () {
    if (this.dbChromaKeyPreviewQualityValue)
      this.chromaKeyPreviewQualityValue = this.dbChromaKeyPreviewQualityValue
    this.video = document.querySelector('#' + this.videoId)
    this.chromacanvas = this.$refs.chromacanvas
    this.deleteme = this.$refs.deleteme
    const that = this
    this.chromacanvas.addEventListener('mousedown', that.doPickColor)
    this.gl = this.chromacanvas.getContext('webgl', { premultipliedAlpha: false, preserveDrawingBuffer: true })
    if (!this.stopRendering) {
      requestAnimationFrame(this.render)
    }
    this.setAttributes()
  },
  destroyed () {
    this.stopRendering = true
    const that = this
    this.chromacanvas.removeEventListener('mousedown', that.doPickColor)
    this.deleteme.removeChild(this.chromacanvas)
    this.chromacanvas = null
    this.gl.getExtension('WEBGL_lose_context').loseContext()
    this.deleteme.innerHTML = ''
    this.video = null
  },
  methods: {
    init () {
      // Check for this.video.getBoundingClientRect().width > 0 to make sure that the video is visible.
      // If the video is not visible boudning rectangle is (0,0,0,0)
      if (this.video && this.video.videoWidth && this.video.videoWidth > 0 && this.video.getBoundingClientRect().width > 0 && this.video.offsetParent) {
        if (!this.initialized && this.initialized2 >= 3) {
          this.updateCanvasSize()
          this.initGeometry()
          this.initShaders()
          this.initTexture()
          this.initialized = true
        }
        if (this.initialized2 < 3) {
          this.initialized2 += 1
        }
      }
    },
    setAttributes () {
      if (!this.chromakey) return
      const rgb = helpers.hexToRGBA(this.chromakey.screen)
      this.gl.uniform1i(this.enabled, this.chromakey.enabled && !this.isPickingMode ? 1 : 0)
      this.gl.uniform4f(this.screen, rgb.r / 255.0, rgb.g / 255.0, rgb.b / 255.0, 1)
      this.gl.uniform1f(this.saturationThreshold, this.chromakey.weight / 100.0)
      this.gl.uniform1f(this.fmaxThreshold, this.chromakey.balance / 100.0)
      this.gl.uniform1f(this.clipBlack, this.chromakey.clipblack / 100.0)
      this.gl.uniform1f(this.clipWhite, this.chromakey.clipwhite / 100.0)
      this.chromacanvas.style.cursor = this.isPickingMode ? 'crosshair' : 'inherit'
    },

    render () {
      if (this.stopRendering) return
      if (this.initialized) {
        if (this.gl.isContextLost()) {
          console.log('webgl context lost ', this.videoId)
        } else {
          this.setAttributes()
          this.updateTexture()
          this.drawScene()
        }
      }
      this.init()
      if (!this.stopRendering) {
        setTimeout(() => {
          requestAnimationFrame(this.render)
        }, 50);
      }
    },

    updateTexture () {
      const level = 0
      const internalFormat = this.gl.RGB
      const srcFormat = this.gl.RGB
      const srcType = this.gl.UNSIGNED_BYTE
      this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture)
      this.gl.texImage2D(this.gl.TEXTURE_2D, level, internalFormat, srcFormat, srcType, this.video)
      if (this.gl.getError()) {
        console.log('init texImage2D for ', this.video.getBoundingClientRect(), ' ', this.video.getClientRects(), ' ',
          this.videoId + ' ' + this.video.videoWidth + ' ' + this.video.currentTime + ' ' +
          this.video.playbackRate + ' ' + this.video.offsetParent)
      }

      /*const t = performance.now()
      console.log('updateTexture ', t - this.lasttext, this.video.playbackRate)
      this.lasttext = t*/
    },
    drawScene () {
      this.gl.viewport(0, 0, this.chromacanvas.width, this.chromacanvas.height)
      this.gl.clearColor(0.0, 0.0, 0.0, 0.0)
      this.gl.clear(this.gl.COLOR_BUFFER_BIT | this.gl.DEPTH_BUFFER_BIT)

      this.gl.activeTexture(this.gl.TEXTURE0)
      this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture)
      this.gl.drawElements(this.gl.TRIANGLES, this.indices.length, this.gl.UNSIGNED_SHORT, 0)

      /*const t = performance.now()
      console.log('drawScene ', t - this.lastrender)
      this.lastrender = t*/
    },

    initGeometry () {
      const vertices = [
        -1, 1, 0.0,
        -1, -1, 0.0,
        1, -1, 0.0,
        1, 1, 0.0
      ]
      this.indices = [3, 2, 1, 3, 1, 0]
      this.vertexBuffer = this.gl.createBuffer()
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.vertexBuffer)
      this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(vertices), this.gl.STATIC_DRAW)
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, null)
      this.indexBuffer = this.gl.createBuffer()
      this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer)
      this.gl.bufferData(this.gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(this.indices), this.gl.STATIC_DRAW)
      this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, null)

      this.textureCoordBuffer = this.gl.createBuffer()
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.textureCoordBuffer)

      const textureCoordinates = [
        0.0, 0.0,
        0.0, 1.0,
        1.0, 1.0,
        1.0, 0.0
      ]

      this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(textureCoordinates), this.gl.STATIC_DRAW)
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.textureCoordBuffer)
    },
    initShaders () {
      const vertShader = this.gl.createShader(this.gl.VERTEX_SHADER)
      this.gl.shaderSource(vertShader, this.vertexShaderSrc)
      this.gl.compileShader(vertShader)
      let success = this.gl.getShaderParameter(vertShader, this.gl.COMPILE_STATUS)
      if (!success) {
        // Something went wrong during compilation; get the error
        console.log('could not compile vertShader:' + this.gl.getShaderInfoLog(vertShader))
      }

      const fragShader = this.gl.createShader(this.gl.FRAGMENT_SHADER)
      this.gl.shaderSource(fragShader, this.fragmentShaderSrc)
      this.gl.compileShader(fragShader)
      success = this.gl.getShaderParameter(fragShader, this.gl.COMPILE_STATUS)
      if (!success) {
        // Something went wrong during compilation; get the error
        console.log('could not compile fragShader:' + this.gl.getShaderInfoLog(fragShader))
      }

      this.shaderProgram = this.gl.createProgram()
      this.gl.attachShader(this.shaderProgram, vertShader)
      this.gl.attachShader(this.shaderProgram, fragShader)
      this.gl.linkProgram(this.shaderProgram)
      this.gl.useProgram(this.shaderProgram)

      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.vertexBuffer)
      this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer)

      const coord = this.gl.getAttribLocation(this.shaderProgram, 'coordinates')
      this.gl.vertexAttribPointer(coord, 3, this.gl.FLOAT, false, 0, 0)
      this.gl.enableVertexAttribArray(coord)

      const num = 2 // every coordinate composed of 2 values
      const type = this.gl.FLOAT // the data in the buffer is 32 bit float
      const normalize = false // don't normalize
      const stride = 0 // how many bytes to get from one set to the next
      const offset = 0 // how many bytes inside the buffer to
      const tcoord = this.gl.getAttribLocation(this.shaderProgram, 'texCoord')
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.textureCoordBuffer)
      this.gl.vertexAttribPointer(tcoord, num, type, normalize, stride, offset)
      this.gl.enableVertexAttribArray(tcoord)

      this.source = this.gl.getUniformLocation(this.shaderProgram, 'source')
      this.gl.uniform1i(this.source, 0)

      this.enabled = this.gl.getUniformLocation(this.shaderProgram, 'enabled')
      this.screen = this.gl.getUniformLocation(this.shaderProgram, 'screen')
      this.saturationThreshold = this.gl.getUniformLocation(this.shaderProgram, 'saturationThreshold')
      this.fmaxThreshold = this.gl.getUniformLocation(this.shaderProgram, 'fmaxThreshold')
      this.clipBlack = this.gl.getUniformLocation(this.shaderProgram, 'clipBlack')
      this.clipWhite = this.gl.getUniformLocation(this.shaderProgram, 'clipWhite')

      this.setAttributes()
    },
    initTexture () {
      this.texture = this.gl.createTexture()
      this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture)
      const level = 0
      const internalFormat = this.gl.RGB
      const srcFormat = this.gl.RGB
      const srcType = this.gl.UNSIGNED_BYTE
      this.gl.texImage2D(this.gl.TEXTURE_2D, level, internalFormat, srcFormat, srcType, this.video)
      if (this.gl.getError()) {
        console.log('init texImage2D for ' + 'err' + ' ' + this.videoId + ' ' + this.video.videoWidth + ' ' + this.video.currentTime + ' ' + this.video.playbackRate + ' ' + this.video.offsetParent)
      }
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE)
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE)
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR)
    },
    updateCanvasSize () {
      this.chromacanvas.width = this.video.videoWidth
      this.chromacanvas.height = this.video.videoHeight
      if (!this.fullres && this.chromaKeyPreviewQualityValue === ChromaKeyPreviewQualityType.Low) {
        while (this.chromacanvas.width >= 320) {
          this.chromacanvas.width /= 2
          this.chromacanvas.height /= 2
        }
      }
    },
    doPickColor (e: MouseEvent) {
      if (!this.isPickingMode || !this.chromacanvas) return
      const rect = this.chromacanvas.getBoundingClientRect()
      const x = this.chromacanvas.width * (e.clientX - rect.left) / rect.width
      const y = this.chromacanvas.height * (rect.height - (e.clientY - rect.top)) / rect.height

      const pixel = new Uint8Array(4)
      this.gl.readPixels(x, y, 1, 1, this.gl.RGBA, this.gl.UNSIGNED_BYTE, pixel)

      // update controls
      const dColor = pixel[2] + 256 * pixel[1] + 65536 * pixel[0]
      const dcolorstr = ('#' + ('0000' + dColor.toString(16)).substr(-6))

      // gl.uniform4f(screen, (pixel[0])/255.0, (pixel[1])/255.0, (pixel[2])/255.0, 1);
      this.$emit('colorPicked', dcolorstr)
    }
  }
})

